import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, CheckCircle2, Eye, EyeOff, KeyRound, Loader2, Lock, Mail, ShieldCheck, Smartphone, User, UserPlus, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import config from "../config";

const INITIAL_FORM = { name: "", mobile: "", email: "", password: "", confirmPassword: "" };
const MOBILE_PATTERN = /^[6-9]\d{9}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_RULES = [
    { key: "length", label: "8+ characters", test: (v) => v.length >= 8 },
    { key: "upper", label: "Uppercase", test: (v) => /[A-Z]/.test(v) },
    { key: "lower", label: "Lowercase", test: (v) => /[a-z]/.test(v) },
    { key: "number", label: "Number", test: (v) => /\d/.test(v) },
    { key: "symbol", label: "Symbol", test: (v) => /[^A-Za-z0-9\s]/.test(v) },
];

function getPasswordState(password) {
    const rules = PASSWORD_RULES.map((rule) => ({ ...rule, passed: rule.test(password) }));
    const score = rules.filter((rule) => rule.passed).length;
    if (!password) return { score, label: "Create a password", color: "bg-border", text: "text-muted-foreground", rules };
    if (score <= 2) return { score, label: "Weak", color: "bg-red-500", text: "text-red-500", rules };
    if (score <= 4) return { score, label: "Good", color: "bg-amber-500", text: "text-amber-500", rules };
    return { score, label: "Strong", color: "bg-emerald-500", text: "text-emerald-500", rules };
}

function validateForm(values) {
    const errors = {};
    const name = values.name.trim();
    if (!name) errors.name = "Please enter your name.";
    else if (name.length > 25) errors.name = "Name must not be greater than 25 characters.";
    else if (!/^[A-Za-z][A-Za-z .'-]*$/.test(name)) errors.name = "Please enter a valid name.";
    if (!MOBILE_PATTERN.test(values.mobile)) errors.mobile = "Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.";
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Please enter a valid email address.";
    if (getPasswordState(values.password).score !== PASSWORD_RULES.length) errors.password = "Please complete all password requirements.";
    if (!values.confirmPassword) errors.confirmPassword = "Please confirm your password.";
    else if (values.password !== values.confirmPassword) errors.confirmPassword = "Passwords do not match.";
    return errors;
}

async function parseJsonResponse(response) {
    const text = await response.text();
    if (!text.trim()) throw new Error(`Server returned an empty response. HTTP ${response.status}.`);
    try { return JSON.parse(text); }
    catch {
        console.error("Invalid registration response:", text);
        throw new Error("The server returned an invalid response. Check the PHP error log.");
    }
}

export default function SignUp({
    endpoint = `${config.API_URL}/signup.php`,
    otpEndpoint = `${config.API_URL}/verify-register-otp.php`,
    loginUrl = "/login",
    onSuccess,
}) {
    const navigate = useNavigate();
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [otpOpen, setOtpOpen] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [otpSuccess, setOtpSuccess] = useState("");
    const [verifyingOtp, setVerifyingOtp] = useState(false);
    const passwordState = useMemo(() => getPasswordState(form.password), [form.password]);
    const hasConfirmation = form.confirmPassword.length > 0;
    const passwordsMatch = hasConfirmation && form.password === form.confirmPassword;

    const handleChange = ({ target: { name, value } }) => {
        let nextValue = value;
        if (name === "mobile") nextValue = value.replace(/\D/g, "").slice(0, 10);
        if (name === "name") nextValue = value.slice(0, 25);
        setForm((current) => ({ ...current, [name]: nextValue }));
        setErrors((current) => ({ ...current, [name]: "" }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (loading) return;
        const validationErrors = validateForm(form);
        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            toast.error("Please correct the highlighted fields.");
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(endpoint, {
                method: "POST", credentials: "include", cache: "no-store",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({
                    name: form.name.trim().toUpperCase(), mobile: form.mobile,
                    email: form.email.trim(), password: form.password,
                    confirm_password: form.confirmPassword,
                }),
            });
            const result = await parseJsonResponse(response);
            if (!response.ok || !result.success) {
                if (result.errors && typeof result.errors === "object") setErrors(result.errors);
                throw new Error(result.message || `Registration failed. HTTP ${response.status}.`);
            }
            toast.success(result.message || "OTP sent successfully.");
            setOtp("");
            setOtpError("");
            setOtpSuccess(result.message || "OTP sent successfully to your email.");
            setOtpOpen(true);
        } catch (error) {
            console.error("Registration error:", error);
            toast.error(error instanceof Error ? error.message : "Unable to connect to the server.");
        } finally { setLoading(false); }
    };

    const handleOtpSubmit = async (event) => {
        event.preventDefault();
        if (verifyingOtp) return;

        if (!/^\d{6}$/.test(otp)) {
            setOtpSuccess("");
            setOtpError("Please enter the complete 6-digit OTP.");
            return;
        }

        setVerifyingOtp(true);
        setOtpError("");
        setOtpSuccess("");

        try {
            const response = await fetch(otpEndpoint, {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ otp }),
            });
            const result = await parseJsonResponse(response);

            if (!response.ok || !result.success) {
                throw new Error(result.message || "OTP verification failed.");
            }

            const successMessage = result.message || "Account created successfully.";
            setOtpSuccess(successMessage);
            toast.success(successMessage);

            window.setTimeout(() => {
                setOtpOpen(false);
                if (typeof onSuccess === "function") onSuccess(result);
                else navigate(result.redirect || loginUrl, { replace: true });
            }, 1400);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Unable to verify OTP.";
            setOtpError(message);
            toast.error(message);
        } finally {
            setVerifyingOtp(false);
        }
    };

    return (
        <section className="relative min-h-[calc(100vh-70px-40px)] flex items-center justify-center px-4 py-8 overflow-hidden" data-testid="signup-page">
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="h-[620px] w-[620px] rounded-full bg-gradient-to-tr from-[#3BC7FF]/20 to-[#7B61FF]/20 blur-3xl" />
            </div>
            <div className="relative w-full max-w-3xl fade-in-up">
                <div className="glass-card rounded-3xl overflow-hidden">
                    <div className="grid md:grid-cols-[0.8fr_1.2fr]">
                        <aside className="relative hidden md:flex flex-col justify-between p-8 text-white bg-gradient-to-br from-[#1769e0] via-[#3154d8] to-[#7B61FF]">
                            <div>
                                <div className="h-12 w-12 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center shadow-lg"><ShieldCheck className="h-6 w-6" /></div>
                                <h2 className="mt-6 text-2xl font-display font-bold">Your services, one secure account.</h2>
                                <p className="mt-3 text-sm leading-6 text-white/75">Register with Smart Solutions to access online forms, services, and your dashboard.</p>
                            </div>
                            <div className="space-y-3 text-sm text-white/85">
                                {["Secure account access", "Fast OTP verification", "Simple service dashboard"].map((item) => (
                                    <div key={item} className="flex items-center gap-3"><span className="h-6 w-6 rounded-full bg-white/15 flex items-center justify-center"><Check className="h-3.5 w-3.5" /></span>{item}</div>
                                ))}
                            </div>
                        </aside>

                        <div className="p-6 sm:p-8">
                            <div className="flex items-start gap-3 mb-6">
                                <div className="h-11 w-11 shrink-0 rounded-2xl btn-gradient flex items-center justify-center text-white shadow-lg md:hidden"><UserPlus className="h-5 w-5" /></div>
                                <div><h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">Create your <span className="text-gradient-brand">account</span></h1><p className="mt-1 text-sm text-muted-foreground">Enter your details to get started securely.</p></div>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                                <Field label="Full Name" error={errors.name} htmlFor="name" hint={`${form.name.length}/25`}>
                                    <InputShell icon={User} error={errors.name}><input id="name" name="name" type="text" value={form.name} onChange={handleChange} disabled={loading} maxLength={25} autoComplete="name" placeholder="Enter your full name" className="signup-input" /></InputShell>
                                </Field>
                                <Field label="Mobile Number" error={errors.mobile} htmlFor="mobile"><InputShell icon={Smartphone} error={errors.mobile}><input id="mobile" name="mobile" type="tel" inputMode="numeric" value={form.mobile} onChange={handleChange} disabled={loading} maxLength={10} autoComplete="tel" placeholder="10-digit mobile" className="signup-input font-mono tracking-wider" /></InputShell></Field>
                                <Field label="Email Address" error={errors.email} htmlFor="email"><InputShell icon={Mail} error={errors.email}><input id="email" name="email" type="email" value={form.email} onChange={handleChange} disabled={loading} autoComplete="email" placeholder="name@example.com" className="signup-input" /></InputShell></Field>

                                <Field label="Password" error={errors.password} htmlFor="password">
                                    <InputShell icon={Lock} error={errors.password}>
                                        <input id="password" name="password" type={showPassword ? "text" : "password"} value={form.password} onChange={handleChange} disabled={loading} autoComplete="new-password" placeholder="Create a strong password" className="signup-input signup-password" />
                                        <PasswordToggle shown={showPassword} onClick={() => setShowPassword((value) => !value)} />
                                    </InputShell>
                                    <div className="mt-3 rounded-xl border border-border/70 bg-background/35 p-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex flex-1 gap-1.5" aria-label={`Password strength: ${passwordState.label}`}>
                                                {[1, 2, 3, 4, 5].map((step) => <span key={step} className={`h-1.5 flex-1 rounded-full transition-colors ${step <= passwordState.score ? passwordState.color : "bg-border"}`} />)}
                                            </div>
                                            <span className={`text-xs font-semibold ${passwordState.text}`}>{passwordState.label}</span>
                                        </div>
                                        <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1.5">
                                            {passwordState.rules.map((rule) => <span key={rule.key} className={`inline-flex items-center gap-1 text-[11px] ${rule.passed ? "text-emerald-500" : "text-muted-foreground"}`}>{rule.passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}{rule.label}</span>)}
                                        </div>
                                    </div>
                                </Field>

                                <Field label="Confirm Password" error={errors.confirmPassword} htmlFor="confirmPassword">
                                    <InputShell icon={Lock} error={errors.confirmPassword || (hasConfirmation && !passwordsMatch)} success={passwordsMatch}>
                                        <input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? "text" : "password"} value={form.confirmPassword} onChange={handleChange} disabled={loading} autoComplete="new-password" placeholder="Enter the same password again" className="signup-input signup-confirm-password" />
                                        {hasConfirmation && (passwordsMatch ? <CheckCircle2 className="absolute right-11 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" /> : <XCircle className="absolute right-11 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />)}
                                        <PasswordToggle shown={showConfirmPassword} onClick={() => setShowConfirmPassword((value) => !value)} />
                                    </InputShell>
                                    {hasConfirmation && <p className={`mt-1.5 flex items-center gap-1.5 text-xs font-medium ${passwordsMatch ? "text-emerald-500" : "text-red-500"}`}>{passwordsMatch ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}{passwordsMatch ? "Passwords match" : "Passwords do not match"}</p>}
                                </Field>

                                <button type="submit" disabled={loading} className="w-full h-12 rounded-full btn-gradient ripple text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-testid="signup-submit-btn">
                                    {loading ? <><Loader2 className="h-4 w-4 animate-spin" />Creating account…</> : <><UserPlus className="h-4 w-4" />Create account</>}
                                </button>
                            </form>
                            <p className="mt-5 text-center text-sm text-muted-foreground">Already have an account? <Link to={loginUrl} className="font-semibold text-primary hover:underline underline-offset-4">Sign in</Link></p>
                        </div>
                    </div>
                </div>
                <p className="mt-4 text-center text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Secured by Smart Solutions</p>
            </div>

            {otpOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="otp-title">
                    <button
                        type="button"
                        aria-label="Close OTP verification"
                        className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm"
                        onClick={() => !verifyingOtp && setOtpOpen(false)}
                    />

                    <div className="relative w-full max-w-sm glass-card rounded-3xl border border-white/10 p-6 sm:p-7 shadow-2xl fade-in-up">
                        <button
                            type="button"
                            aria-label="Close"
                            disabled={verifyingOtp}
                            onClick={() => setOtpOpen(false)}
                            className="absolute right-4 top-4 h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-foreground/[0.06] transition disabled:opacity-50"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        <div className="mx-auto h-14 w-14 rounded-2xl btn-gradient flex items-center justify-center text-white shadow-lg">
                            <KeyRound className="h-6 w-6" />
                        </div>
                        <div className="mt-4 text-center">
                            <h2 id="otp-title" className="text-xl font-display font-bold">Verify your email</h2>
                            <p className="mt-1.5 text-sm text-muted-foreground">
                                Enter the 6-digit OTP sent to <span className="font-medium text-foreground">{form.email}</span>
                            </p>
                        </div>

                        <form onSubmit={handleOtpSubmit} className="mt-5" noValidate>
                            <label htmlFor="registration-otp" className="sr-only">Six-digit OTP</label>
                            <input
                                id="registration-otp"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                autoFocus
                                maxLength={6}
                                value={otp}
                                disabled={verifyingOtp || Boolean(otpSuccess && /^\d{6}$/.test(otp))}
                                onChange={(event) => {
                                    setOtp(event.target.value.replace(/\D/g, "").slice(0, 6));
                                    setOtpError("");
                                    setOtpSuccess("");
                                }}
                                placeholder="• • • • • •"
                                className={`w-full h-14 rounded-2xl bg-background/60 border text-center text-2xl font-mono font-bold tracking-[0.45em] outline-none transition ${otpError ? "border-red-500 focus:ring-2 focus:ring-red-500/20" : otpSuccess ? "border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" : "border-border focus:border-primary focus:ring-2 focus:ring-primary/25"}`}
                            />

                            {otpError && (
                                <div className="mt-3 flex items-start gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2.5 text-xs text-red-500" role="alert">
                                    <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span>{otpError}</span>
                                </div>
                            )}

                            {otpSuccess && (
                                <div className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2.5 text-xs text-emerald-500" role="status">
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span>{otpSuccess}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={verifyingOtp || otp.length !== 6}
                                className="mt-4 w-full h-12 rounded-full btn-gradient ripple text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {verifyingOtp ? <><Loader2 className="h-4 w-4 animate-spin" />Verifying OTP…</> : <><ShieldCheck className="h-4 w-4" />Verify &amp; Create Account</>}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .signup-input { width:100%; height:3rem; padding-left:2.75rem; padding-right:1rem; border-radius:.75rem; background:hsl(var(--background) / .6); border:1px solid hsl(var(--border)); outline:none; font-size:.875rem; transition:border-color 160ms ease,box-shadow 160ms ease,opacity 160ms ease; }
                .signup-input:focus { border-color:hsl(var(--primary)); box-shadow:0 0 0 2px hsl(var(--primary) / .25); }
                .signup-input:disabled { opacity:.6; cursor:not-allowed; }
                .signup-input::placeholder { color:hsl(var(--muted-foreground) / .7); }
                .signup-input.signup-password { padding-right:2.75rem; }
                .signup-input.signup-confirm-password { padding-right:5rem; }
            `}</style>
        </section>
    );
}

function Field({ label, error, hint, htmlFor, children }) {
    return <div><div className="flex items-center justify-between gap-3"><label htmlFor={htmlFor} className="text-xs font-semibold tracking-[0.16em] uppercase text-muted-foreground">{label}</label>{hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}</div><div className="mt-2">{children}</div>{error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}</div>;
}

function InputShell({ icon: Icon, error, success, children }) {
    return <div className="relative"><Icon className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 ${error ? "text-red-500" : success ? "text-emerald-500" : "text-muted-foreground"}`} />{children}</div>;
}

function PasswordToggle({ shown, onClick }) {
    return <button type="button" onClick={onClick} aria-label={shown ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-foreground/[0.06] transition">{shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>;
}
